import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    ImageList,
    ImageListItem,
    ImageListItemBar,
} from '@mui/material';
import { 
    Delete,
    Upload
} from '@mui/icons-material';
import { useNotify, useTranslate, useRecordContext } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

interface ImageItem {
    url: string;
    caption?: string;
    order: number;
}

interface MultiImageUploadFieldProps {
    source: string;
    value?: ImageItem[];
    onChange: (value: ImageItem[]) => void;
    label?: string;
    maxImages?: number;
    helperText?: string;
    siteId?: string;
}

export const MultiImageUploadField: React.FC<MultiImageUploadFieldProps> = ({
    source,
    value = [],
    onChange,
    label,
    maxImages = 10,
    helperText,
    siteId: propSiteId
}) => {
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const notify = useNotify();
    const translate = useTranslate();
    const record = useRecordContext();
    
    // Get siteId from props or record (for edit mode)
    const siteId = propSiteId || record?.id || 'temp-' + Date.now();

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        // Check max images limit
        if (value.length + files.length > maxImages) {
            setUploadError(translate('resources.sites.errors.maxImagesExceeded', { max: maxImages }));
            return;
        }

        // Clear previous error
        setUploadError(null);
        setUploading(true);

        const newImages: ImageItem[] = [];
        
        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];

                // Validate file type
                const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
                if (!allowedTypes.includes(file.type)) {
                    continue; // Skip invalid files
                }

                // Validate file size (5MB limit)
                const maxSize = 5 * 1024 * 1024; // 5MB
                if (file.size > maxSize) {
                    continue; // Skip large files
                }

                const formData = new FormData();
                formData.append('file', file);
                formData.append('siteId', String(siteId));

                const token = await tokenManager.getAccessToken();
                if (!token) {
                    notify(translate('ra.auth.auth_check_error'), { type: 'error' });
                    continue;
                }
                
                const response = await fetch(`${API_CONFIG.baseURL}/api/backend/sites/upload-image`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });

                const data = await response.json();

                if (response.ok) {
                    // If multiple URLs are returned (for different sizes), use the primary one
                    const imageUrl = data.url || (data.urls && data.urls.original);
                    newImages.push({
                        url: imageUrl,
                        caption: '',
                        order: value.length + newImages.length
                    });
                }
            }

            if (newImages.length > 0) {
                onChange([...value, ...newImages]);
                notify('resources.sites.notifications.imagesUploaded', { 
                    type: 'success',
                    messageArgs: { count: newImages.length }
                });
            }
        } catch (error: any) {
            console.error('Upload error:', error);
            setUploadError(error.message || translate('resources.sites.errors.uploadFailed'));
            notify(error.message || 'Upload failed', { type: 'error' });
        } finally {
            setUploading(false);
        }

        // Reset the input
        event.target.value = '';
    };

    const handleRemove = (index: number) => {
        const newImages = value.filter((_, i) => i !== index);
        // Reorder remaining images
        const reorderedImages = newImages.map((img, i) => ({ ...img, order: i }));
        onChange(reorderedImages);
        notify('resources.sites.notifications.imageRemoved', { type: 'info' });
    };

    const handleCaptionChange = (index: number, caption: string) => {
        const newImages = [...value];
        newImages[index] = { ...newImages[index], caption };
        onChange(newImages);
    };

    return (
        <Box sx={{ mb: 2 }}>
            {label && (
                <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {label} ({value.length}/{maxImages})
                </Typography>
            )}
            
            <Box sx={{ mb: 2 }}>
                <input
                    id={`${source}-upload`}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    disabled={uploading || value.length >= maxImages}
                />
                <label htmlFor={`${source}-upload`}>
                    <Button
                        variant="outlined"
                        component="span"
                        disabled={uploading || value.length >= maxImages}
                        startIcon={uploading ? <CircularProgress size={20} /> : <Upload />}
                    >
                        {translate('resources.sites.actions.uploadImages')}
                    </Button>
                </label>
            </Box>

            {value.length > 0 && (
                <ImageList sx={{ width: '100%', height: 200 }} cols={2} rowHeight={164}>
                    {value.map((item, index) => (
                        <ImageListItem key={index}>
                            <img
                                src={item.url}
                                alt={item.caption || `Image ${index + 1}`}
                                loading="lazy"
                                style={{ height: '100%', objectFit: 'cover' }}
                            />
                            <ImageListItemBar
                                title={
                                    <input
                                        type="text"
                                        placeholder={translate('resources.sites.fields.imageCaption')}
                                        value={item.caption || ''}
                                        onChange={(e) => handleCaptionChange(index, e.target.value)}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'white',
                                            width: '100%',
                                            outline: 'none'
                                        }}
                                    />
                                }
                                actionIcon={
                                    <IconButton
                                        sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                        onClick={() => handleRemove(index)}
                                        size="small"
                                    >
                                        <Delete />
                                    </IconButton>
                                }
                            />
                        </ImageListItem>
                    ))}
                </ImageList>
            )}
            
            {uploadError && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setUploadError(null)}>
                    {uploadError}
                </Alert>
            )}
            
            {helperText && (
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};